import { useState } from 'react';
import { Box, Button, Chip, Typography, useTheme } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRemoveProductMutation } from '@/apis/extended/productApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { GlassCard } from '@/components/ui/GlassCard';
import { formatNumber } from '@/utils/helper';
import { Product } from '@/types/ProductType';
import { CommonEditIconButton } from '../Common/CommonButton/CommonEditIconButton';
import { CommonRemoveIconButton } from '../Common/CommonButton/CommonRemoveIconButton';
import { EditProductDetailModal } from '../ConfirmModals/EditProductDetailModal';
import { RemoveProductModal } from '../ConfirmModals/RemoveProductModal';
import { ProductBadges } from './ProductBadges';

type ProductDetailBasicProps = {
  product: Product;
};

type ProductDetailPurchasePriceProps = {
  product: Product;
  sx?: SxProps<Theme>;
};

export const ProductDetailPurchasePrice = ({
  product,
  sx,
}: ProductDetailPurchasePriceProps) => {
  const theme = useTheme();
  const listPrice = product.price;
  const offerPrice = product.loanInfo?.offerPrice;
  const purchasePrice = offerPrice ?? listPrice;

  return (
    <Box sx={sx}>
      <Typography
        variant="h2"
        sx={{
          fontWeight: 700,
          background: `linear-gradient(135deg, ${
            theme.palette.primary.main
          } 0%, ${alpha(theme.palette.primary.main, 0.75)} 55%, ${
            theme.palette.secondary.main
          } 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontSize: { xs: '1.75rem', sm: '2.125rem' },
          textAlign: { xs: 'center', sm: 'left' },
        }}
      >
        $ {formatNumber(purchasePrice)}
      </Typography>
    </Box>
  );
};

export const ProductDetailBasic = ({ product }: ProductDetailBasicProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: user } = useGetCurrentUserQuery();
  const [removeProduct] = useRemoveProductMutation();

  const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);
  const [isRemoveProductModalOpen, setIsRemoveProductModalOpen] =
    useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  if (!product || !user) return null;

  const handleRemoveProduct = () => {
    removeProduct(product._id);
    if (location.pathname.includes('admin')) navigate('/admin/product');
    else navigate('/main');
  };

  const listPrice = product.price;
  const offerPrice = product.loanInfo?.offerPrice;
  const hasOfferDiff =
    Number.isFinite(offerPrice) &&
    Number.isFinite(listPrice) &&
    offerPrice !== listPrice;

  const chipSx = {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.greyShades.border1}`,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  } as const;

  return (
    <GlassCard variant="subtle" sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
            lineHeight: 1.3,
            textAlign: { xs: 'center', sm: 'left' },
            flex: 1,
            minWidth: 0,
          }}
        >
          {product.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <CommonEditIconButton
            onClick={() => setIsEditProductModalOpen(true)}
          />
          <CommonRemoveIconButton
            onClick={() => setIsRemoveProductModalOpen(true)}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.6,
            fontSize: { xs: '0.9rem', sm: '1rem' },
          }}
        >
          {showFullDescription
            ? product.description
            : product.description.length > 200
            ? `${product.description.substring(0, 200)}...`
            : product.description}
        </Typography>
        {product.description.length > 200 && (
          <Button
            onClick={() => setShowFullDescription(!showFullDescription)}
            sx={{
              mt: 1,
              color: theme.palette.primary.main,
              textTransform: 'none',
              p: 0,
              minWidth: 'auto',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
                opacity: 0.85,
              },
            }}
          >
            {showFullDescription ? 'See less' : 'See more'}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'flex-end' },
          gap: '8px',
          mt: 2,
          flexWrap: 'wrap',
        }}
      >
        <ProductBadges product={product} />
      </Box>
      {hasOfferDiff && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            sx={chipSx}
            variant="outlined"
            label={`List Price: $${formatNumber(listPrice)}`}
          />
          <Chip
            sx={chipSx}
            variant="outlined"
            label={`Offer Amount: $${formatNumber(offerPrice || 0)}`}
          />
        </Box>
      )}
      <EditProductDetailModal
        isOpen={isEditProductModalOpen}
        product={product}
        onClose={() => setIsEditProductModalOpen(false)}
      />
      <RemoveProductModal
        isOpen={isRemoveProductModalOpen}
        onClose={() => setIsRemoveProductModalOpen(false)}
        onConfirm={handleRemoveProduct}
      />
    </GlassCard>
  );
};
